// src/components/report/manifestTracking/ManifestTrackingFilters.js
import React, { useEffect, useState } from 'react';
import {
    Autocomplete, Button, Grid, MenuItem, Paper, Stack, TextField,
} from '@mui/material';
import {
    RestartAlt as RestartAltIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
} from '@mui/icons-material';

import { useManifestTrackingCatalogs } from '../../../hooks/report/useManifestTrackingReport';

const ManifestTrackingFilters = ({ filters, onApply, onReset, isFetching }) => {
    const [localFilters, setLocalFilters] = useState(filters);
    const { data: catalogs, isLoading: loadingCatalogs } = useManifestTrackingCatalogs();

    const vehiculos = catalogs?.vehiculos || [];
    const conductores = catalogs?.conductores || [];
    const serviciosProyecto = catalogs?.serviciosProyecto || [];
    const stateOptions = catalogs?.stateOptions || [];

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleChange = (field, value) => {
        setLocalFilters((prev) => ({ ...prev, [field]: value }));
    };

    const findVehiculo = (id) =>
        vehiculos.find((v) => String(v.id) === String(id)) || null;

    const findConductor = (id) =>
        conductores.find((c) => String(c.id) === String(id)) || null;

    const findServicioProyecto = (id) =>
        serviciosProyecto.find((s) => String(s.id) === String(id)) || null;

    return (
        <Paper
            elevation={0}
            sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
        >
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Buscar por código"
                        placeholder="Ej: MN-000123 o 123"
                        value={localFilters.search || ''}
                        onChange={(e) => handleChange('search', e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />,
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        label="Fecha desde"
                        type="date"
                        fullWidth
                        size="small"
                        value={localFilters.dateFrom || ''}
                        onChange={(e) => handleChange('dateFrom', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        label="Fecha hasta"
                        type="date"
                        fullWidth
                        size="small"
                        value={localFilters.dateTo || ''}
                        onChange={(e) => handleChange('dateTo', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                    <TextField
                        select
                        label="Estado"
                        fullWidth
                        size="small"
                        value={localFilters.manEst || ''}
                        onChange={(e) => handleChange('manEst', e.target.value)}
                    >
                        {stateOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        size="small"
                        options={vehiculos}
                        loading={loadingCatalogs}
                        value={findVehiculo(localFilters.vehID)}
                        onChange={(_, opt) => handleChange('vehID', opt?.id || '')}
                        getOptionLabel={(opt) =>
                            `${opt.matricula || ''} ${opt.descripcion ? `· ${opt.descripcion}` : ''}`.trim()
                        }
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderInput={(params) => (
                            <TextField {...params} label="Vehículo" placeholder="Todos" />
                        )}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        size="small"
                        options={conductores}
                        loading={loadingCatalogs}
                        value={findConductor(localFilters.empID)}
                        onChange={(_, opt) => handleChange('empID', opt?.id || '')}
                        getOptionLabel={(opt) => opt.nombre || ''}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderInput={(params) => (
                            <TextField {...params} label="Conductor" placeholder="Todos" />
                        )}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                    <Autocomplete
                        size="small"
                        options={serviciosProyecto}
                        loading={loadingCatalogs}
                        value={findServicioProyecto(localFilters.serProID)}
                        onChange={(_, opt) => handleChange('serProID', opt?.id || '')}
                        getOptionLabel={(opt) =>
                            `${opt.nombre || ''}${opt.proyecto_nombre ? ` · ${opt.proyecto_nombre}` : ''}`
                        }
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderInput={(params) => (
                            <TextField {...params} label="Servicio-proyecto" placeholder="Todos" />
                        )}
                    />
                </Grid>

                <Grid item xs={12} md={5}>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                            variant="outlined"
                            startIcon={<RestartAltIcon />}
                            onClick={onReset}
                        >
                            Limpiar
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<RefreshIcon />}
                            onClick={() => onApply(localFilters)}
                            disabled={isFetching}
                        >
                            Actualizar
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default ManifestTrackingFilters;